import { useEffect, useRef, useState } from 'react';
import type { CachedShot } from '@flm9/sync';
import { requeueShot } from '../lib/sync';

const UNDO_MS = 6_000;

/**
 * "Schuss gelöscht – Rückgängig" after a delete in the keypad sheets (UX
 * review REF-21). Deleting stays one tap; the undo queues the same shot
 * again through the outbox. Hides itself after ~6 s.
 */
export function ShotUndoToast({
  shot,
  shotCount,
  onDone,
  onRestored,
}: {
  /** The shot just deleted; null hides the toast. */
  shot: CachedShot | null;
  shotCount: number;
  onDone: () => void;
  onRestored: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (!shot) return;
    const t = window.setTimeout(() => onDoneRef.current(), UNDO_MS);
    return () => window.clearTimeout(t);
  }, [shot]);

  if (!shot) return null;

  async function undo() {
    if (!shot || busy) return;
    setBusy(true);
    try {
      const restored = await requeueShot(shot, shotCount);
      if (!restored) alert('Rückgängig nicht möglich — alle Schüsse dieser Runde sind erfasst.');
      else onRestored();
    } catch (err) {
      console.error('undo delete failed', err);
      alert('Rückgängig fehlgeschlagen — bitte den Schuss neu erfassen.');
    } finally {
      setBusy(false);
      onDone();
    }
  }

  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 shadow-xl"
    >
      <span>Schuss #{shot.ord} gelöscht</span>
      <button
        type="button"
        onClick={() => void undo()}
        disabled={busy}
        className="min-h-12 rounded px-3 font-bold text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
      >
        Rückgängig
      </button>
    </div>
  );
}
